/**
 * "Three orders ticked. Delete them."
 *
 * The bar over a studio's order list, drawn only while something is ticked, so a list
 * nobody is ticking looks as it always did (the call queue's bar works the same way). The
 * delete asks first, in the kit's dialog, and then says in one line what came of it: how
 * many went, how many stayed because they changed in between, and a test invoice the CRM
 * did not let go, which the desk then deletes by hand.
 *
 * While only some of the page's orders are ticked, the bar offers the rest of them at
 * once. That is the phone's only way to it: a phone folds the list into cards and hides the
 * column head with its box (`crm-list-stack`).
 */
import { useState } from "react";
import { CheckCheck, Trash2, X } from "lucide-react";
import { ConfirmDialog } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { OrdersRemoved } from "../../../../lib/cardOrders";
import { Btn, useToast } from "../../../ui";
import { pagePicks, removalCounts, type PickableRow } from "./lib";
import type { OrderPicks } from "./useOrderPicks";

/** A button that takes half of the phone's second line (and all of it alone). */
const HALF = "max-[560px]:order-2 max-[560px]:grow max-[560px]:basis-[calc(50%-5px)] max-[560px]:justify-center";

export function OrderPicksBar({
  picks,
  rows,
  remove,
  onRemoved,
}: {
  picks: OrderPicks;
  /** The rows of the page, for "select all". */
  rows: readonly PickableRow[];
  /** The studio's own delete. */
  remove: (ids: string[]) => Promise<OrdersRemoved>;
  /** After a delete that reached the server: the list loads again. */
  onRemoved: (result: OrdersRemoved) => void;
}) {
  const t = useT();
  const toast = useToast();
  // The ids the question is about, fixed when it opens: the ticks are cleared on success
  // while the dialog still fades out, and its words must not count down to zero meanwhile
  const [asked, setAsked] = useState<{ ids: string[]; open: boolean }>({ ids: [], open: false });
  const [busy, setBusy] = useState(false);
  const page = pagePicks(rows, picks.picked);

  async function confirm() {
    setBusy(true);
    try {
      const result = await remove(asked.ids);
      const n = removalCounts(result);
      // One line: the toast holds one message at a time, and a second would hide the first
      const said = [
        n.removed > 0 ? t("sales.orderPicks.done", { n: n.removed }) : "",
        n.kept > 0 ? t("sales.orderPicks.changed", { n: n.kept }) : "",
        n.invoicesLeft > 0 ? t("sales.orderPicks.invoiceLeft", { n: n.invoicesLeft }) : "",
      ];
      toast(said.filter(Boolean).join(" "));
      picks.clear();
      onRemoved(result);
    } catch {
      toast(t("sales.orderPicks.failed"));
    } finally {
      setBusy(false);
      setAsked((now) => ({ ...now, open: false }));
    }
  }

  return (
    <>
      {picks.size > 0 ? (
        // A desk: the count, then the three steps in a row on the right. A phone (the
        // CRM's 560px fold): the count and a bare X on the first line, "select all" and
        // the delete as two wide halves under it, where the thumb is
        <div className="crm-card mb-3.5 flex flex-wrap items-center gap-2.5 p-3.5 pl-5">
          {/* On a phone the count claims the first line beside the X, or "select all" would squeeze in there */}
          <span className="crm-tnum min-w-0 flex-1 text-[13px] font-bold max-[560px]:basis-[calc(100%-60px)]">{t("sales.orderPicks.selected", { n: picks.size })}</span>
          {page.some ? (
            <Btn small className={HALF} onClick={() => picks.setMany(page.ids, true)}>
              <CheckCheck className="size-3.5" />
              {t("sales.orderPicks.pickAll")}
            </Btn>
          ) : null}
          <Btn small className="max-[560px]:px-3.5" onClick={picks.clear}>
            <X className="size-3.5" />
            <span className="max-[560px]:sr-only">{t("sales.orderPicks.clear")}</span>
          </Btn>
          <Btn small tone="danger" className={`${HALF} max-[560px]:order-3`} onClick={() => setAsked({ ids: picks.ids, open: true })}>
            <Trash2 className="size-3.5" />
            {t("sales.orderPicks.delete")}
          </Btn>
        </div>
      ) : null}

      <ConfirmDialog
        open={asked.open}
        title={t("sales.orderPicks.confirmTitle", { n: asked.ids.length })}
        description={t("sales.orderPicks.confirmBody", { n: asked.ids.length })}
        confirmLabel={t("sales.orderPicks.delete")}
        cancelLabel={t("common.cancel")}
        variant="destructive"
        loading={busy}
        onConfirm={() => void confirm()}
        onCancel={() => setAsked((now) => ({ ...now, open: false }))}
      />
    </>
  );
}
